/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import { useState, useEffect } from 'react';
import ApiFactory from 'utils/api-factory';
import { cloneDeep } from 'lodash';
import { APITypes, getAPIType } from '../utils/APITypes';

export async function _parse(api) {
  const { Parser } = await import('@asyncapi/parser');
  const { AvroSchemaParser } = await import('@asyncapi/avro-schema-parser');
  const { OpenAPISchemaParser } = await import('@asyncapi/openapi-schema-parser');

  const parser = new Parser({});
  parser.registerSchemaParser(AvroSchemaParser());
  parser.registerSchemaParser(OpenAPISchemaParser());

  const validateOptions = { allowedSeverity: { error: true, warning: true } };
  const { document, diagnostics } = await parser.parse(api, { validateOptions });
  if (!document) {
    console.error('Failed to parse api');
    console.error(diagnostics);
  }
  return document;
}

export async function _validate(api) {
  const { Parser } = await import('@asyncapi/parser');
  const { AvroSchemaParser } = await import('@asyncapi/avro-schema-parser');
  const { OpenAPISchemaParser } = await import('@asyncapi/openapi-schema-parser');

  const parser = new Parser({});
  parser.registerSchemaParser(AvroSchemaParser());
  parser.registerSchemaParser(OpenAPISchemaParser());

  return parser.validate(api);
}

const errorDetails = (error) => {
  const { title = '', validationErrors = [], detail = '' } = error;
  const validationMessages = validationErrors.map(({ title }) => title);
  const errorDetails = [title, detail, ...validationMessages];
  return errorDetails;
};

const containsExternalRefs = (root) => {
  if (typeof root !== 'object' || root === null) return [];

  const seen = new WeakSet();
  const areExternal = [];
  const stack = [root];
  const isExternalUrl = (ref) => {
    try {
      const url = new URL(ref);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  while (stack.length > 0) {
    const node = stack.pop();

    if (typeof node === 'object' && node !== null && !seen.has(node)) {
      seen.add(node);

      if (typeof node.$ref === 'string' && !node.$ref.startsWith('#')) {
        if (isExternalUrl(node.$ref)) {
          areExternal.push(`$ref: ${node.$ref}`);
        }
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const key in node) {
        if (Object.hasOwn(node, key)) {
          stack.push(node[key]);
        }
      }
    }
  }
  return areExternal;
};

const useCreateApiHandler = (api) => {
  const [{ originalApi, apiHandler, loading, error }, updateState] = useState({ originalApi: api, apiHandler: null, loading: true, error: null });

  useEffect(() => {
    updateState((prev) => ({ ...prev, loading: true }));
    let cancel = false;
    const externalRefs = containsExternalRefs(api);
    if (externalRefs.length) {
      updateState((prev) => ({
        ...prev,
        error: { type: 'external_ref', errorString: externalRefs.join('\n') },
        loading: false
      }));
      return;
    }
    if (getAPIType(api) === APITypes.asyncapi) {
      (async () => {
        try {
          const clonedApi = cloneDeep(api);
          const res = await _parse(api);

          if (cancel) return;
          updateState((prev) => ({ ...prev, originalApi: clonedApi, apiHandler: ApiFactory(res), error: null, loading: false }));
        } catch (error) {
          if (cancel) return;
          console.error('Errors detected while parsing:', error);
          const details = { type: 'async_parse_errors', errorString: errorDetails(error).join('\n') };
          updateState((prev) => ({ ...prev, error: details, loading: false }));
        }
      })();
    } else {
      updateState((prev) => ({ ...prev, originalApi: api, apiHandler: ApiFactory(api), error: null, loading: false }));
    }
    return () => {
      cancel = true;
    };
  }, [api]);

  return { originalApi, apiHandler, loading, error };
};

export { useCreateApiHandler };
